import { MutationCache, QueryCache, QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { isApiError } from "@magarac/shared/errors";
import { createRouter, RouterProvider } from "@tanstack/react-router";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { ErrorScreen } from "./components/ErrorScreen";
import { routeTree } from "./routeTree.gen";
import "./styles.css";

const PUBLIC_PATHS = ["/login", "/signup", "/pay/", "/accept-invite/", "/q/", "/i/"];

/**
 * Any request can reveal that things changed underneath the screen:
 * - 401: the session ended (expired, signed out elsewhere) -> sign in, then come back here.
 * - 403 or 404: the person's role or membership may have changed (made a member, removed)
 *   -> reload their businesses and role so the menus and screens match what they may do.
 */
function onApiError(err: unknown) {
  if (!isApiError(err)) return;
  const path = window.location.pathname;
  if (err.status === 401 && !PUBLIC_PATHS.some((p) => path.startsWith(p))) {
    const back = `${path}${window.location.search}`;
    window.location.assign(`/login?redirect=${encodeURIComponent(back)}`);
  } else if (err.status === 403 || err.status === 404) {
    void queryClient.invalidateQueries({ queryKey: ["me", "tenants"] });
  }
}

const queryClient: QueryClient = new QueryClient({
  // Refetch on focus: a screen left open for hours (calendar, a job's photo links, a slot
  // list) catches up when the person comes back. Forms keep their own local state.
  defaultOptions: { queries: { retry: 1, staleTime: 10_000, refetchOnWindowFocus: true } },
  queryCache: new QueryCache({ onError: onApiError }),
  mutationCache: new MutationCache({ onError: onApiError }),
});

const router = createRouter({
  routeTree,
  context: { queryClient },
  defaultPreload: "intent",
  scrollRestoration: true,
  defaultErrorComponent: ErrorScreen,
});

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <RouterProvider router={router} />
    </QueryClientProvider>
  </StrictMode>,
);
